import {
  parseApiResponseError,
  ApiErrorCodes,
} from '../../../../api/api-error';
import {
  RecuperoUsernameError,
  RecuperoUsernameErrorCodiciErroreEnum,
} from '../../../../api/generated';
import commonErrorMessages from '../../../../messages/common-messages/common-error.messages';
import retrieveUsernameMessages from '../../../../messages/user-access/retrieveUsername.messages';
import { ErrorMessages } from '../../../../model/error';
import {
  isEmailValid,
  isPhoneNumberValid,
  isSquareAgreementCodeValid,
  isUserCodeValid,
} from '../../../../utils/validators';
import {
  RetrieveUsernameError,
  RetrieveUsernameOptions,
  RetrieveUsernameParams,
  RetrieveUsernameState,
} from './retrieve-username-types';

/**
 * Method that verifies first access page form values
 * @param formValues Values inserted by the user in the first access page
 * @returns undefined if everything is ok, or a RetrieveUsernameError otherwise
 */
export const verifyRetrieveUsernameCredentials = (
  request: RetrieveUsernameParams,
  state: RetrieveUsernameState
): RetrieveUsernameError | undefined => {

  const {
    userCode,
    squareAgreementCode,
    isRequestFromMultipleMatchPage: multipleMatchesFound,
  } = request;
  const { selectedOption, userEmail, userPhoneNumber } = state;
  if (
    selectedOption === RetrieveUsernameOptions.BySMS &&
    !isPhoneNumberValid(userPhoneNumber)
  ) {
    return 'INVALID_PHONE_NUMBER_FORMAT';
  } else if (
    selectedOption === RetrieveUsernameOptions.ByMail &&
    !isEmailValid(userEmail)
  ) {
    return 'INVALID_EMAIL_FORMAT';
  } else if (
    multipleMatchesFound === true &&
    !squareAgreementCode &&
    !userCode
  ) {
    return 'FIELD_NOT_FILL';
  } else if (
    multipleMatchesFound === true &&
    squareAgreementCode &&
    !isSquareAgreementCodeValid(squareAgreementCode)
  ) {
    return 'FIELD_NOT_VALID';
  } else if (
    multipleMatchesFound === true &&
    userCode &&
    !isUserCodeValid(userCode)
  ) {
    return 'FIELD_NOT_VALID';
  }
  return undefined;
};

export const messagesForRetrieveUsernameError = (
  error: RetrieveUsernameError,
  selectedOption?:any
): ErrorMessages => {
  switch (error) {
    case 'INVALID_EMAIL_FORMAT':
      return {
        title: retrieveUsernameMessages.errorTitleInvalidMailFormat,
      };
    case 'INVALID_PHONE_NUMBER_FORMAT':
      return {
        title: retrieveUsernameMessages.errorTitleInvalidPhoneNumberFormat,
      };
    case 'EMAIL_NOT_FOUND':
      return { title: retrieveUsernameMessages.errorTitleEmailNotFound };
    case 'PHONE_NUMBER_NOT_FOUND':
      return { title: retrieveUsernameMessages.errorTitlePhoneNumberNotFound };
    case 'FIELD_NOT_VALID':
      return { title: retrieveUsernameMessages.errorTitleFieldNotValid };
    case 'FIELD_NOT_FILL':
      return { title: retrieveUsernameMessages.errorTitleFieldNotFill };
    case 'INVALID_SQUARE_AGREEMENT_CODE':
      return {
        title: retrieveUsernameMessages.messageErrorInvalidSquareAgreementCode,
      };
    case 'NO_ASSOCIATED_USERS':
      if(selectedOption===RetrieveUsernameOptions.ByMail){
        return {
          title: retrieveUsernameMessages.messageErrorModalInvalidUser,
        };
      }else if(selectedOption===RetrieveUsernameOptions.BySMS){
        return {
          title: retrieveUsernameMessages.errorTitlePhoneNumberNotFound,
        };
      }else{
        return {
          title:retrieveUsernameMessages.errorNoAssociatedUser
        }
      }

    case 'TOO_MANY_REQUESTS':
      return { title: retrieveUsernameMessages.errorTitleTooManyRequests };

    case 'GENERIC_ERROR':
    default:
      return {
        title: commonErrorMessages.errorTitleDefault,
        description: commonErrorMessages.errorDescriptionDefault,
      };
  }
};

export const getRetrieveUsernameError = (
  error: unknown,
  state: RetrieveUsernameState,
  params: RetrieveUsernameParams
): RetrieveUsernameError => {
  const apiError = parseApiResponseError<RecuperoUsernameError>(error);
  const errorCodes = apiError.payload?.codiciErrore;

  switch (apiError.responseErrorCode) {
    case ApiErrorCodes.NOT_FOUND:
      return state.selectedOption === RetrieveUsernameOptions.ByMail
        ? 'EMAIL_NOT_FOUND'
        : state.selectedOption === RetrieveUsernameOptions.BySMS
        ? 'PHONE_NUMBER_NOT_FOUND'
        : 'GENERIC_ERROR';

    case ApiErrorCodes.BAD_REQUEST:
      if (errorCodes?.includes(RecuperoUsernameErrorCodiciErroreEnum.CorrispondenzaMultipla)) {
        return params.isRequestFromMultipleMatchPage ? 'GENERIC_ERROR' : 'MULTIPLE_MATCH';
      }

      if (errorCodes?.includes(RecuperoUsernameErrorCodiciErroreEnum.NessunaUtenzaAssociata)) {
        return 'NO_ASSOCIATED_USERS';
      }

      return 'FIELD_NOT_VALID';

    case ApiErrorCodes.TOO_MANY_REQUESTS:
      return 'TOO_MANY_REQUESTS';

    default:
      return 'GENERIC_ERROR';
  }
};

